import http from "./httpService";
import { getClientUid } from "@/utils/clientUid";
import cookiesService from "@/services/cookiesServiceNoAwait";

/**
 * @typedef {import('../types/api.js').BaseApiResponse} BaseApiResponse
 * @typedef {import('../types/api.js').ChatbotResponseData} ChatbotResponseData
 * @typedef {import('../types/api.js').QuestionItem} QuestionItem
 */

class ChatbotService {
  /**
   * Send a message to the chatbot
   * @param {Object} params - Message parameters
   * @param {string} params.query - User query message
   * @param {string} params.sessionId - session id of the message
   * @param {string} params.location - User's timezone location (e.g., "Asia/Singapore")
   * @param {string} params.pageId - Current page identifier
   * @param {string|null} params.activityName - Activity name (can be null)
   * @returns {Promise<BaseApiResponse & { data: ChatbotResponseData }>} Chatbot response
   */
  static async sendMessage({
    query,
    sessionId,
    location,
    pageId,
    activityName = null,
  }) {
    const clientUid = getClientUid();

    const response = await http.post(
      "/chatbot/informative-agent",
      {
        query,
        session_id: sessionId,
        location,
        page_id: pageId,
        activity_name: activityName,
      },
      {
        headers: {
          "client-uid": clientUid,
        },
      }
    );
    return response.data;
  }

  /**
   * Send a message to the chatbot with streaming response
   * @param {Object} params - Message parameters
   * @param {string} params.query - User query message
   * @param {string} params.sessionId - session id of the message
   * @param {Function} params.onChunk - Callback function to handle each chunk of data
   * @returns {Promise<void>}
   */
  static async sendMessageStreaming({
    query,
    sessionId,
    location,
    pageId,
    activityName,
    onChunk,
  }) {
    const apiUrl = `${process.env.VUE_APP_API_URL}/api/chatbot/informative-agent-stream`;
    const clientUid = getClientUid();
    const authToken = cookiesService.getCookie("authToken");

    const headers = {
      "Content-Type": "application/json",
      "client-uid": clientUid,
    };

    if (authToken?.token) {
      headers["Authorization"] = `Bearer ${authToken.token}`;
    }

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers,
        body: JSON.stringify({
          query,
          session_id: sessionId,
          location,
          page_id: pageId,
          activity_name: activityName,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let streamDone = false;

      while (!streamDone) {
        const { done, value } = await reader.read();
        streamDone = done;

        if (streamDone) break;

        // Decode the chunk and pass it directly
        const chunk = decoder.decode(value, { stream: true });

        if (chunk) {
          try {
            // Try to parse as JSON if it looks like JSON
            if (chunk.trim().startsWith("{") || chunk.trim().startsWith("[")) {
              const jsonData = JSON.parse(chunk.trim());
              onChunk(jsonData);
            } else {
              // Pass the raw text chunk with preserved formatting
              onChunk(chunk);
            }
          } catch (e) {
            // If parsing fails, pass the raw text with preserved formatting
            onChunk(chunk);
          }
        }
      }
    } catch (error) {
      console.error("Error in streaming request:", error);
      throw error;
    }
  }

  /**
   * Get suggested questions for a specific page
   * @param {string} pageName - Page name to get questions for
   * @returns {Promise<BaseApiResponse & { data: QuestionItem[] }>} Response with array of question items
   */
  static async getQuestions(pageName) {
    const response = await http.get(`/chatbot/questions?pages=${pageName}`);
    return response.data;
  }
}

export default ChatbotService;
